import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { GhostIcon, MessageCircleIcon, UserIcon } from "lucide-react";
import { useMessagesStore } from "stores/messages";

import type { UserMe } from "simplydo/interfaces/types/userTypes";
import { MSTeamsIcon } from "simplydo/react/ui/brand-icon";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import BaseChip, {
  type ChipAction,
  type ChipConfiguration,
  type ChipDetail,
} from "./BaseChip";
import type { ChipUser } from "./types";
import { useSocialLinks } from "./useSocialLinks";

type IUserChipBase = { user?: ChipUser | null; showSocials?: boolean };

type IUserChip = IUserChipBase & ChipConfiguration;

function UserChip({
  user: userProp,
  actionIcon,
  actionClick,
  actionTooltip,
  showSocials,
  ...props
}: IUserChip) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();

  const params = useParams();
  const matchId = params?.id;

  let user: ChipUser =
    userProp ?? ({ profile: { fullName: "Unknown User" } } as ChipUser);
  const hasProfile = user?.profile;
  if (!hasProfile) {
    user = { profile: { fullName: "Unknown User" } } as ChipUser;
  }

  const socials = useSocialLinks(hasProfile ? user : null);

  const { jobTitle = "", department = "", tagline = "" } = user.profile ?? {};
  let description = jobTitle ?? "";
  if (department) {
    if (jobTitle) {
      description += " - ";
    }
    description += department;
  }
  if (!description && tagline) {
    description = tagline;
  }

  const permissionUser: UserMe | undefined = currentUser ?? undefined;

  const userCanListUsers =
    util.hasPermission(
      permissionUser,
      "org.listUsers",
      currentUser?.ownerOrganisation?._id,
    ) || util.hasPermission(permissionUser, "group.listUsers", matchId);

  const userCanViewEmails = util.hasPermission(
    permissionUser,
    "org.viewUserEmails",
    currentUser?.ownerOrganisation?._id,
  );

  const messageUser = (member: ChipUser) => {
    api.messages.findThreadByUser(
      member._id,
      (data) => {
        const rawThread = data?.thread;
        const existingThread = Array.isArray(rawThread)
          ? rawThread[0]
          : rawThread;
        useMessagesStore
          .getState()
          .selectThread(
            existingThread || {
              _id: `newThread:${member._id}`,
              participantUsers: [member],
            },
          );
      },
      (err) => toast.error(err.message),
    );
  };

  const canMessage =
    user._id !== currentUser?._id &&
    currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "directMessaging",
    );
  const teamsHandle = user?.teamsHandle;
  const canMessageViaTeams =
    user._id !== currentUser?._id &&
    !!currentUser?.ownerOrganisation?.enabledFeatures?.includes(
      "teamsHandleAdvertising",
    ) &&
    !!teamsHandle;

  const messageUserViaTeams = () => {
    if (!teamsHandle) return;
    const teamsUrl = `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(teamsHandle)}`;
    window.open(teamsUrl, "_blank", "noopener,noreferrer");
  };

  const ghost = () => {
    util
      .confirm(
        t("users.chip.ghostConfirmTitle"),
        t("users.chip.ghostConfirmText"),
      )
      .then(() =>
        api.users.ghost(
          user?._id,
          () => {
            api.invalidateQueries();
            navigate("/");
          },
          (err) => toast.error(err.message),
        ),
      )
      .catch(() => {});
  };

  const canGhost =
    user?._id &&
    !(user?._id === currentUser?._id) &&
    util.hasPermission(
      permissionUser,
      "super.ghostUser",
      currentUser?.ownerOrganisation?._id,
    );

  const actions: ChipAction[] = [];
  if (canMessage) {
    actions.push({
      key: "message",
      label: t("users.chip.message"),
      icon: MessageCircleIcon,
      onClick: () => messageUser(user),
    });
  }
  if (canMessageViaTeams) {
    actions.push({
      key: "teams",
      label: t("users.chip.messageViaTeams"),
      icon: MSTeamsIcon,
      onClick: messageUserViaTeams,
    });
  }
  if (userCanListUsers) {
    actions.push({
      key: "profile",
      label: t("users.chip.viewProfile"),
      icon: UserIcon,
      onClick: () => navigate(`/users/${user?._id}`),
    });
  }
  if (canGhost) {
    actions.push({
      key: "ghost",
      label: t("users.chip.ghost"),
      icon: GhostIcon,
      onClick: ghost,
      danger: true,
    });
  }

  const details: ChipDetail[] = [];
  const email = user.emails?.[0]?.address;
  if (userCanViewEmails && email) {
    details.push({ label: t("users.chip.email"), value: email });
  }
  const samlMappings =
    currentUser?.ownerOrganisation?.auth?.saml?.customMappings ?? [];
  const customMappings = Array.isArray(samlMappings)
    ? samlMappings
    : Object.keys(samlMappings).map((localKey) => ({ localKey }));
  customMappings.forEach(
    (mapping: { localKey?: string; displayName?: string }) => {
      if (!mapping.localKey) return;
      const value = user?.profile?.attributes?.[mapping.localKey];
      if (value) {
        details.push({ label: mapping.displayName ?? mapping.localKey, value });
      }
    },
  );

  return (
    <BaseChip
      {...props}
      title={user.profile?.fullName}
      subtitle={description}
      about={description !== tagline ? tagline : undefined}
      details={details}
      actions={actions}
      socials={socials}
      showSocials={showSocials}
      popupDisabled={!hasProfile}
      imageUrl={util.avatarUrl(user, "200x200")}
      labels={user.iconRoles}
      actionIcon={actionIcon}
      actionClick={actionClick}
      actionTooltip={actionClick ? actionTooltip : undefined}
    />
  );
}

export default UserChip;
